import React, { useState } from 'react';
import { useSimulationStore } from '../../store/useSimulationStore';
import { useCanvasStore } from '../../store/useCanvasStore';
import { StructureCanvas } from '../canvas/StructureCanvas';
import { CanvasControls } from '../canvas/CanvasControls';
import { MetricsGrid } from '../dashboard/MetricsGrid';
import { GoalTracker } from '../dashboard/GoalTracker';
import { CheckBreakdownTable } from '../financial/CheckBreakdownTable';
import { PathwayControls } from '../controls/PathwayControls';
import { MonthSlider } from '../controls/MonthSlider';
import { SearchBar } from '../controls/SearchBar';
import { MainTabType } from './NavigationTabs';
import { Tabs } from '../ui/Tabs';
import { Button } from '../ui/Button';
import { findNodeById } from '../../utils/treeUtils';
import { calculateNodePayout } from '../../math/payout';
import { saveStructureToCloud } from '../../services/structureLibrary';
import {
  SlidersHorizontal,
  BarChart3,
  FileText,
  UserPlus,
  Edit,
  FolderKanban,
  BookmarkCheck,
  Save,
  Check,
  Printer,
  ArrowRightLeft,
  Network,
  Rocket,
  RotateCcw,
} from 'lucide-react';

export interface ResponsiveContainerProps {
  activeMobileTab: MainTabType;
  onOpenNodeEditor: (nodeId: string) => void;
  onOpenPresets: () => void;
  onOpenLibrary?: () => void;
  onOpenDocumentExport?: () => void;
}

export const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  activeMobileTab,
  onOpenNodeEditor,
  onOpenPresets,
  onOpenLibrary,
  onOpenDocumentExport,
}) => {
  // Store bindings
  const mode = useSimulationStore((state) => state.mode);
  const selectedMonth = useSimulationStore((state) => state.selectedMonth);
  const setSelectedMonth = useSimulationStore((state) => state.setSelectedMonth);
  const autoPathwayConfig = useSimulationStore((state) => state.autoPathwayConfig);
  const updateAutoPathwayConfig = useSimulationStore((state) => state.updateAutoPathwayConfig);
  const getCurrentRootNode = useSimulationStore((state) => state.getCurrentRootNode);
  const getCurrentPayoutBreakdown = useSimulationStore((state) => state.getCurrentPayoutBreakdown);
  const getCurrentStep = useSimulationStore((state) => state.getCurrentStep);
  const simulationResult = useSimulationStore((state) => state.simulationResult);
  const addSandboxNode = useSimulationStore((state) => state.addSandboxNode);
  const pathwayInitialTree = useSimulationStore((state) => state.pathwayInitialTree);
  const activeStructureTitle = useSimulationStore((state) => state.activeStructureTitle);
  const transferPathwayToSandbox = useSimulationStore((state) => state.transferPathwayToSandbox);
  const transferSandboxToPathway = useSimulationStore((state) => state.transferSandboxToPathway);
  const resetPathwayToNewbie = useSimulationStore((state) => state.resetPathwayToNewbie);

  const selectedNodeId = useCanvasStore((state) => state.selectedNodeId);

  const [isQuickSaving, setIsQuickSaving] = useState(false);
  const [quickSaveSuccess, setQuickSaveSuccess] = useState(false);

  // Desktop collapsible sidebars
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(true);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 1280);

  // Desktop right-panel tab
  const [rightPanelTab, setRightPanelTab] = useState<'metrics' | 'check'>('metrics');

  // Tablet right-panel tab
  const [tabletTab, setTabletTab] = useState<'params' | 'metrics' | 'check'>('params');

  const rootNode = getCurrentRootNode();
  const payout = getCurrentPayoutBreakdown();
  const currentStep = getCurrentStep();

  const handleQuickSave = async () => {
    if (isQuickSaving) return;
    setIsQuickSaving(true);
    try {
      const title = activeStructureTitle || `${rootNode.name} (Р${rootNode.level})`;
      await saveStructureToCloud(rootNode, title);
      setQuickSaveSuccess(true);
      setTimeout(() => setQuickSaveSuccess(false), 2000);
    } catch (e) {
      console.warn('Quick save error:', e);
    } finally {
      setIsQuickSaving(false);
    }
  };

  // Previous month income for trend badge (calculated with active commission coefficient)
  const storeRateCoeff = useSimulationStore((state) => state.commissionRateCoefficient);
  const usdExchangeRate = useSimulationStore((state) => state.usdExchangeRate);

  const prevStep =
    mode === 'pathway' && selectedMonth > 1 && simulationResult?.steps[selectedMonth - 2]
      ? simulationResult.steps[selectedMonth - 2]
      : null;
  const previousMonthGrossIncome = prevStep
    ? calculateNodePayout(prevStep.rootConsultant, usdExchangeRate, storeRateCoeff).totalGrossIncomeUAH
    : undefined;

  const selectedNode = selectedNodeId ? findNodeById(rootNode, selectedNodeId) : null;

  const handleAddChild = (parentId: string) => {
    const newNode = addSandboxNode(parentId);
    if (newNode) {
      onOpenNodeEditor(newNode.id);
    }
  };

  return (
    <main className="w-full flex-1 flex overflow-hidden relative">
      {/* =========================================================================
          DESKTOP PRO WORKBENCH (>= 1024px)
          ========================================================================= */}
      <div className="hidden lg:flex w-full flex-1 overflow-hidden relative">
        {/* LEFT PANEL: Controls, Search, Month Timeline, Pathway Parameters */}
        {isLeftSidebarOpen && (
          <aside className="w-[320px] xl:w-[350px] h-full flex flex-col border-r border-white/10 bg-[#0A0F1D]/90 backdrop-blur-xl overflow-hidden flex-shrink-0 transition-all duration-300 z-20 shadow-2xl">
            <div className="p-3 border-b border-white/10 flex-shrink-0 flex items-center justify-between gap-2">
              <div className="flex-1">
                <SearchBar rootNode={rootNode} onSelectNode={onOpenNodeEditor} />
              </div>
              <button
                type="button"
                onClick={() => setIsLeftSidebarOpen(false)}
                className="w-8 h-8 rounded-lg bg-surface-hover hover:bg-slate-800 text-text-secondary hover:text-white flex items-center justify-center border border-white/10 transition-colors cursor-pointer"
                title="Згорнути ліву панель"
              >
                ◀
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3.5 custom-scrollbar">
              {mode === 'pathway' ? (
                <>
                  <div data-tour="timeline-controls">
                    <MonthSlider
                      currentMonth={selectedMonth}
                      maxMonths={autoPathwayConfig.months}
                      onChange={setSelectedMonth}
                    />
                  </div>

                  {/* Transfer Pathway snapshot to Sandbox */}
                  <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                        <ArrowRightLeft className="w-3.5 h-3.5 text-accent-neon" />
                        <span>Знімок у Конструктор</span>
                      </span>
                      {pathwayInitialTree && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                          З конструктора
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-text-muted leading-relaxed">
                      Перенести поточну структуру {selectedMonth}-го місяця в конструктор для детального редагування ліній.
                    </p>
                    <Button
                      variant="outline"
                      fullWidth
                      size="sm"
                      leftIcon={<Network className="w-4 h-4 text-cyan-400" />}
                      onClick={transferPathwayToSandbox}
                    >
                      Перенести в конструктор
                    </Button>
                    {pathwayInitialTree && (
                      <button
                        type="button"
                        onClick={resetPathwayToNewbie}
                        className="w-full text-center text-[10px] text-text-muted hover:text-red-400 transition-colors py-0.5 flex items-center justify-center gap-1 cursor-pointer"
                      >
                        <RotateCcw className="w-3 h-3" />
                        <span>Скинути до новачка (з 0)</span>
                      </button>
                    )}
                  </div>

                  <PathwayControls
                    config={autoPathwayConfig}
                    onChange={updateAutoPathwayConfig}
                  />
                </>
              ) : (
                <div className="space-y-3.5">
                  <div className="glass-panel rounded-2xl p-4 border border-white/10 space-y-3">
                    <h3 className="text-sm font-bold text-text-primary flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-cyan-400" />
                      Керування конструктором
                    </h3>
                    <p className="text-xs text-text-secondary leading-relaxed">
                      Клікайте на будь-якого консультанта або кнопку <strong>(+)</strong> на мапі для додавання та налаштування ліній.
                    </p>

                    <div className="pt-2 flex flex-col gap-2">
                      {/* Transfer Sandbox structure to Pathway Simulation */}
                      <Button
                        variant="primary"
                        fullWidth
                        size="sm"
                        leftIcon={<Rocket className="w-4 h-4" />}
                        onClick={transferSandboxToPathway}
                      >
                        Симулювати в Шляху новачка
                      </Button>

                      <Button
                        variant="outline"
                        fullWidth
                        size="sm"
                        leftIcon={<UserPlus className="w-4 h-4 text-accent-neon" />}
                        onClick={() => handleAddChild(rootNode.id)}
                      >
                        Додати нову пряму гілку (+)
                      </Button>

                      <Button
                        variant="outline"
                        fullWidth
                        size="sm"
                        leftIcon={quickSaveSuccess ? <Check className="w-4 h-4 text-accent-neon" /> : <Save className={`w-4 h-4 text-accent-neon ${isQuickSaving ? 'animate-spin' : ''}`} />}
                        onClick={handleQuickSave}
                        disabled={isQuickSaving}
                      >
                        {quickSaveSuccess ? 'Збережено версію!' : 'Швидке збереження (1-клік)'}
                      </Button>

                      <div className="grid grid-cols-2 gap-1.5 pt-1">
                        {onOpenLibrary && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<BookmarkCheck className="w-3.5 h-3.5 text-amber-400" />}
                            onClick={onOpenLibrary}
                          >
                            Бібліотека
                          </Button>
                        )}

                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<FolderKanban className="w-3.5 h-3.5 text-cyan-400" />}
                          onClick={onOpenPresets}
                        >
                          Шаблони
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </aside>
        )}

        {/* Left Sidebar Open Tab (when collapsed) */}
        {!isLeftSidebarOpen && (
          <button
            type="button"
            onClick={() => setIsLeftSidebarOpen(true)}
            className="absolute left-4 top-4 z-30 px-3.5 py-2 rounded-xl bg-slate-900/90 border border-white/15 text-text-primary hover:border-accent-emerald/50 shadow-xl backdrop-blur-xl text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer hover:bg-slate-800"
            title="Розгорнути панель параметрів"
          >
            <span>▶</span>
            <span>Параметри</span>
          </button>
        )}

        {/* CENTER PANEL: Interactive SVG Canvas Viewport */}
        <section data-tour="structure-canvas" className="flex-1 relative h-full bg-[#0A0F1D] overflow-hidden flex flex-col">
          <StructureCanvas
            onNodeSelect={(id) => onOpenNodeEditor(id)}
            onAddChild={(parentId) => handleAddChild(parentId)}
          />

          {/* Floating Canvas Controls HUD (Bottom Center) */}
          <CanvasControls />

          {/* Floating Selected Node Quick Pill */}
          {selectedNode && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 animate-fadeIn">
              <button
                type="button"
                onClick={() => onOpenNodeEditor(selectedNode.id)}
                className="
                  flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900/90 border border-accent-emerald/40
                  shadow-[0_0_20px_rgba(16,185,129,0.3)] backdrop-blur-xl text-xs font-semibold
                  text-text-primary hover:bg-slate-800 transition-all cursor-pointer
                "
              >
                <Edit className="w-3.5 h-3.5 text-accent-neon" />
                <span>Обрано: {selectedNode.name} (Р{selectedNode.level}) — Редагувати</span>
              </button>
            </div>
          )}
        </section>

        {/* Right Sidebar Open Tab (when collapsed) */}
        {!isRightSidebarOpen && (
          <button
            type="button"
            onClick={() => setIsRightSidebarOpen(true)}
            className="absolute right-4 top-4 z-30 px-3.5 py-2 rounded-xl bg-slate-900/90 border border-white/15 text-text-primary hover:border-accent-neon/50 shadow-xl backdrop-blur-xl text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer hover:bg-slate-800"
            title="Розгорнути аналітику"
          >
            <span>Аналітика</span>
            <span>◀</span>
          </button>
        )}

        {/* RIGHT PANEL: Executive KPIs, Dynamic Goal Tracker & Financial Statement */}
        {isRightSidebarOpen && (
          <aside className="w-[360px] xl:w-[400px] h-full flex flex-col border-l border-white/10 bg-[#0A0F1D]/90 backdrop-blur-xl overflow-hidden flex-shrink-0 transition-all duration-300 z-20 shadow-2xl">
            {/* Panel Tabs Header */}
            <div className="p-2.5 border-b border-white/10 flex-shrink-0 flex items-center justify-between gap-2">
              <div data-tour="right-panel-tabs" className="flex-1">
                <Tabs
                  items={[
                    { id: 'metrics', label: 'Показники & Цілі', icon: <BarChart3 className="w-3.5 h-3.5" /> },
                    { id: 'check', label: 'Деталізація чеку', icon: <FileText className="w-3.5 h-3.5" /> },
                  ]}
                  activeId={rightPanelTab}
                  onChange={(id) => setRightPanelTab(id as 'metrics' | 'check')}
                  variant="segmented"
                  fullWidth
                  size="sm"
                />
              </div>

              <button
                type="button"
                onClick={() => setIsRightSidebarOpen(false)}
                className="w-8 h-8 rounded-lg bg-surface-hover hover:bg-slate-800 text-text-secondary hover:text-white flex items-center justify-center border border-white/10 transition-colors cursor-pointer"
                title="Згорнути праву панель"
              >
                ▶
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-3.5 custom-scrollbar">
              {rightPanelTab === 'metrics' ? (
                <>
                  <MetricsGrid
                    rootNode={rootNode}
                    payout={payout}
                    activeCount={currentStep?.activeConsultantsCount}
                    totalCount={currentStep?.totalConsultantsCount}
                    previousMonthGrossIncome={previousMonthGrossIncome}
                  />
                  <GoalTracker
                    rootNode={rootNode}
                    currentIncomeUAH={payout.totalGrossIncomeUAH}
                  />
                </>
              ) : (
                <CheckBreakdownTable
                  payout={payout}
                  simulationMonth={mode === 'pathway' ? selectedMonth : undefined}
                  onSelectLeader={(id) => onOpenNodeEditor(id)}
                />
              )}
            </div>
          </aside>
        )}
      </div>

      {/* =========================================================================
          TABLET VIEWPORT (768px - 1023px)
          ========================================================================= */}
      <div className="hidden md:flex lg:hidden w-full flex-1 overflow-hidden">
        {/* Left 55% Canvas */}
        <div data-tour="structure-canvas" className="w-[55%] h-full relative border-r border-white/10">
          <StructureCanvas
            onNodeSelect={(id) => onOpenNodeEditor(id)}
            onAddChild={(parentId) => handleAddChild(parentId)}
          />
          <CanvasControls />
        </div>

        {/* Right 45% Intelligence & Settings */}
        <div className="w-[45%] h-full flex flex-col border-l border-white/10 bg-[#0A0F1D] overflow-hidden">
          <div className="p-3 border-b border-white/10 flex-shrink-0 space-y-2.5">
            <SearchBar rootNode={rootNode} onSelectNode={onOpenNodeEditor} />

            <div data-tour="right-panel-tabs" className="w-full">
              <Tabs
                items={[
                  { id: 'params', label: 'Параметри', icon: <SlidersHorizontal className="w-3.5 h-3.5" /> },
                  { id: 'metrics', label: 'Аналітика', icon: <BarChart3 className="w-3.5 h-3.5" /> },
                  { id: 'check', label: 'Чек', icon: <FileText className="w-3.5 h-3.5" /> },
                ]}
                activeId={tabletTab}
                onChange={(id) => setTabletTab(id as 'params' | 'metrics' | 'check')}
                variant="segmented"
                fullWidth
                size="sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3.5 space-y-3.5 custom-scrollbar">
            {tabletTab === 'params' && (
              <>
                {/* Quick Action Strip for Tablet */}
                <div className="flex items-center gap-2">
                  {mode === 'pathway' ? (
                    <Button
                      variant="outline"
                      fullWidth
                      size="sm"
                      leftIcon={<ArrowRightLeft className="w-3.5 h-3.5 text-accent-neon" />}
                      onClick={transferPathwayToSandbox}
                    >
                      У конструктор
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      fullWidth
                      size="sm"
                      leftIcon={<Rocket className="w-3.5 h-3.5" />}
                      onClick={transferSandboxToPathway}
                    >
                      Симулювати шлях
                    </Button>
                  )}

                  {onOpenDocumentExport && (
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Printer className="w-3.5 h-3.5 text-accent-emerald" />}
                      onClick={onOpenDocumentExport}
                    >
                      Документ
                    </Button>
                  )}
                </div>

                {mode === 'pathway' ? (
                  <>
                    <div data-tour="timeline-controls">
                      <MonthSlider
                        currentMonth={selectedMonth}
                        maxMonths={autoPathwayConfig.months}
                        onChange={setSelectedMonth}
                      />
                    </div>
                    <PathwayControls
                      config={autoPathwayConfig}
                      onChange={updateAutoPathwayConfig}
                    />
                  </>
                ) : (
                  <div className="glass-panel rounded-2xl p-4 border border-white/10 space-y-3">
                    <h3 className="text-sm font-bold text-text-primary flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-cyan-400" />
                      Керування конструктором
                    </h3>
                    <p className="text-xs text-text-secondary leading-relaxed">
                      Клікайте на будь-якого консультанта або кнопку <strong>(+)</strong> на мапі для додавання та налаштування ліній.
                    </p>

                    <div className="pt-2 flex flex-col gap-2">
                      <Button
                        variant="outline"
                        fullWidth
                        size="sm"
                        leftIcon={<UserPlus className="w-4 h-4 text-accent-neon" />}
                        onClick={() => handleAddChild(rootNode.id)}
                      >
                        Додати нову пряму гілку (+)
                      </Button>

                      <div className="grid grid-cols-2 gap-1.5 pt-1">
                        {onOpenLibrary && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<BookmarkCheck className="w-3.5 h-3.5 text-amber-400" />}
                            onClick={onOpenLibrary}
                          >
                            Бібліотека
                          </Button>
                        )}

                        <Button
                          variant="secondary"
                          size="sm"
                          leftIcon={<FolderKanban className="w-3.5 h-3.5 text-cyan-400" />}
                          onClick={onOpenPresets}
                        >
                          Шаблони
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {tabletTab === 'metrics' && (
              <>
                <MetricsGrid
                  rootNode={rootNode}
                  payout={payout}
                  activeCount={currentStep?.activeConsultantsCount}
                  totalCount={currentStep?.totalConsultantsCount}
                  previousMonthGrossIncome={previousMonthGrossIncome}
                />
                <GoalTracker
                  rootNode={rootNode}
                  currentIncomeUAH={payout.totalGrossIncomeUAH}
                />
              </>
            )}

            {tabletTab === 'check' && (
              <CheckBreakdownTable
                payout={payout}
                simulationMonth={mode === 'pathway' ? selectedMonth : undefined}
                onSelectLeader={(id) => onOpenNodeEditor(id)}
              />
            )}
          </div>
        </div>
      </div>

      {/* =========================================================================
          MOBILE VIEWPORT (320px - 767px): Active Tab Switched Content
          ========================================================================= */}
      <div className="flex md:hidden w-full flex-1 overflow-hidden flex-col">
        {activeMobileTab === 'map' && (
          <div data-tour="structure-canvas" className="w-full h-full relative">
            <div className="absolute top-2 left-2 right-2 z-30">
              <SearchBar rootNode={rootNode} onSelectNode={onOpenNodeEditor} />
            </div>
            <StructureCanvas
              onNodeSelect={(id) => onOpenNodeEditor(id)}
              onAddChild={(parentId) => handleAddChild(parentId)}
            />
            <CanvasControls />
          </div>
        )}

        {activeMobileTab === 'metrics' && (
          <div className="w-full h-full overflow-y-auto p-3.5 pb-24 space-y-4 custom-scrollbar">
            <MetricsGrid
              rootNode={rootNode}
              payout={payout}
              activeCount={currentStep?.activeConsultantsCount}
              totalCount={currentStep?.totalConsultantsCount}
              previousMonthGrossIncome={previousMonthGrossIncome}
            />
            <GoalTracker
              rootNode={rootNode}
              currentIncomeUAH={payout.totalGrossIncomeUAH}
            />
          </div>
        )}

        {activeMobileTab === 'check' && (
          <div className="w-full h-full overflow-y-auto p-3.5 pb-24 space-y-4 custom-scrollbar">
            <CheckBreakdownTable
              payout={payout}
              simulationMonth={mode === 'pathway' ? selectedMonth : undefined}
              onSelectLeader={(id) => onOpenNodeEditor(id)}
            />
          </div>
        )}

        {activeMobileTab === 'settings' && (
          <div className="w-full h-full overflow-y-auto p-3.5 pb-24 space-y-4 custom-scrollbar">
            <SearchBar rootNode={rootNode} onSelectNode={onOpenNodeEditor} />

            {/* Quick Export & Library buttons on mobile */}
            <div className="grid grid-cols-2 gap-2">
              {onOpenDocumentExport && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<Printer className="w-3.5 h-3.5 text-accent-emerald" />}
                  onClick={onOpenDocumentExport}
                >
                  Документ PDF
                </Button>
              )}

              {onOpenLibrary && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<BookmarkCheck className="w-3.5 h-3.5 text-amber-400" />}
                  onClick={onOpenLibrary}
                >
                  Бібліотека
                </Button>
              )}
            </div>

            {mode === 'pathway' ? (
              <>
                <div className="glass-panel rounded-2xl p-3 border border-white/10 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-text-primary flex items-center gap-1.5">
                      <ArrowRightLeft className="w-3.5 h-3.5 text-accent-neon" />
                      <span>Знімок у Конструктор</span>
                    </span>
                    {pathwayInitialTree && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300">
                        З конструктора
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-text-muted leading-relaxed">
                    Перенести структуру {selectedMonth}-го місяця в конструктор для редагування.
                  </p>
                  <Button
                    variant="outline"
                    fullWidth
                    size="sm"
                    leftIcon={<Network className="w-4 h-4 text-cyan-400" />}
                    onClick={transferPathwayToSandbox}
                  >
                    Перенести в конструктор
                  </Button>
                  {pathwayInitialTree && (
                    <button
                      type="button"
                      onClick={resetPathwayToNewbie}
                      className="w-full text-center text-[11px] text-text-muted hover:text-red-400 transition-colors py-1 cursor-pointer"
                    >
                      Скинути до новачка (з 0)
                    </button>
                  )}
                </div>

                <div data-tour="timeline-controls">
                  <MonthSlider
                    currentMonth={selectedMonth}
                    maxMonths={autoPathwayConfig.months}
                    onChange={setSelectedMonth}
                  />
                </div>
                <PathwayControls
                  config={autoPathwayConfig}
                  onChange={updateAutoPathwayConfig}
                />
              </>
            ) : (
              <div className="space-y-3">
                <Button
                  variant="primary"
                  fullWidth
                  size="sm"
                  leftIcon={<Rocket className="w-4 h-4" />}
                  onClick={transferSandboxToPathway}
                >
                  Симулювати в Шляху новачка
                </Button>

                <Button
                  variant="outline"
                  fullWidth
                  leftIcon={<FolderKanban className="w-4 h-4 text-cyan-400" />}
                  onClick={onOpenPresets}
                >
                  Шаблони готових команд
                </Button>

                <Button
                  variant="outline"
                  fullWidth
                  leftIcon={<UserPlus className="w-4 h-4 text-accent-neon" />}
                  onClick={() => handleAddChild(rootNode.id)}
                >
                  Додати пряму лінію (+)
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};
